import { z } from 'zod';

/**
 * Common DTOs for shared patterns across all endpoints
 * Includes pagination, sorting, filtering, responses, and param validation
 */

// ============================================================================
// SHARED VALIDATION HELPERS
// ============================================================================

/**
 * Money / decimal string regex — accepts non-negative numbers with up to two
 * decimal places (cents precision). Used for any wire-format Decimal column
 * (loan amounts, sale prices, vendor fees, comparables prices, etc.) where
 * we want to preserve precision over JSON without dropping to floats.
 *
 *   "0"        ✓  "1234"     ✓  "1234.5"   ✓  "1234.56"  ✓
 *   "-1"       ✗  "1234.567" ✗  "1.2.3"    ✗  ".5"       ✗  ""  ✗
 */
const MONEY_STRING_REGEX = /^\d+(\.\d{1,2})?$/;

/**
 * Factory for a money/decimal Zod schema with a custom error message.
 * Use this anywhere a Decimal column ships over the wire as a string.
 *
 * @example
 * ```ts
 * const PriceField = moneyString('Invalid currency amount');
 * const Schema = z.object({ amount: PriceField });
 * ```
 */
export const moneyString = (errorMessage = 'Invalid money amount') =>
  z.string().regex(MONEY_STRING_REGEX, errorMessage);

/**
 * Default money/decimal Zod schema. Two-decimal precision, non-negative.
 */
export const MoneyString = moneyString();

// ============================================================================
// REQUEST VALIDATION
// ============================================================================

/**
 * UUID Path Parameter Validation
 * Use for all :id route parameters that require strict UUID format
 */
export const UuidParamSchema = z.object({
  id: z.string().uuid({ message: 'Invalid UUID format' }),
});

export type UuidParam = z.infer<typeof UuidParamSchema>;

/**
 * Flexible ID Path Parameter Validation
 * Accepts both UUIDs and slug-style identifiers (e.g., "pacific-properties")
 * Use for resources that may have human-readable IDs (like demo businesses)
 */
export const IdOrSlugParamSchema = z.object({
  id: z
    .string()
    .min(1, { message: 'ID is required' })
    .max(100, { message: 'ID is too long' })
    .regex(/^[a-zA-Z0-9-]+$/, {
      message: 'ID must contain only letters, numbers, and hyphens',
    }),
});

export type IdOrSlugParam = z.infer<typeof IdOrSlugParamSchema>;

/**
 * Pagination Query Parameters
 * Standard pagination for all list endpoints
 */
export const PaginationRequestSchema = z.object({
  page: z.preprocess(
    (val) => (typeof val === 'number' ? String(val) : val),
    z
      .string()
      .optional()
      .default('1')
      .transform((val) => parseInt(val, 10))
      .pipe(z.number().int().positive()),
  ),
  limit: z.preprocess(
    (val) => (typeof val === 'number' ? String(val) : val),
    z
      .string()
      .optional()
      .default('20')
      .transform((val) => parseInt(val, 10))
      .pipe(z.number().int().positive().max(100, 'Limit cannot exceed 100')),
  ),
});

export type PaginationRequest = z.infer<typeof PaginationRequestSchema>;

/**
 * Sort Query Parameters
 * Standard sorting for all list endpoints
 */
export const SortRequestSchema = z.object({
  sortBy: z.string().optional(),
  sortOrder: z.enum(['asc', 'desc']).optional().default('desc'),
});

export type SortRequest = z.infer<typeof SortRequestSchema>;

/**
 * Combined Query Parameters for List Endpoints
 * Includes pagination, sorting, and search
 */
export const ListQuerySchema = PaginationRequestSchema.merge(SortRequestSchema).extend({
  search: z.string().optional(),
});

export type ListQuery = z.infer<typeof ListQuerySchema>;

/**
 * Date Range Filter
 * For filtering by createdAt, updatedAt, etc.
 */
export const DateRangeSchema = z.object({
  startDate: z.string().datetime().optional(),
  endDate: z.string().datetime().optional(),
});

export type DateRange = z.infer<typeof DateRangeSchema>;

// ============================================================================
// RESPONSE ENVELOPES
// ============================================================================

/**
 * Pagination Metadata
 * Returned with all paginated responses
 */
export const PaginationMetaSchema = z.object({
  page: z.number().int().positive(),
  limit: z.number().int().positive(),
  total: z.number().int().nonnegative(),
  totalPages: z.number().int().nonnegative(),
  hasNextPage: z.boolean(),
  hasPreviousPage: z.boolean(),
});

export type PaginationMeta = z.infer<typeof PaginationMetaSchema>;

/**
 * Paginated Response Wrapper
 * Generic type for all paginated list endpoints
 */
export const createPaginatedResponseSchema = <T extends z.ZodTypeAny>(dataSchema: T) => {
  return z.object({
    data: z.array(dataSchema),
    meta: PaginationMetaSchema,
  });
};

// Helper type for paginated responses
export type PaginatedResponse<T> = {
  data: T[];
  meta: PaginationMeta;
};

/**
 * Success Response (for operations that don't return data)
 */
export const SuccessResponseSchema = z.object({
  success: z.boolean().default(true),
  message: z.string().optional(),
});

export type SuccessResponse = z.infer<typeof SuccessResponseSchema>;

/**
 * Error Detail
 * Individual validation error
 */
export const ErrorDetailSchema = z.object({
  path: z.string(),
  message: z.string(),
  code: z.string(),
});

export type ErrorDetail = z.infer<typeof ErrorDetailSchema>;

/**
 * Error Response
 * Standardized error format for all API errors
 */
export const ErrorResponseSchema = z.object({
  statusCode: z.number().int(),
  message: z.string(),
  error: z.string().optional(),
  errors: z.array(ErrorDetailSchema).optional(),
  timestamp: z.string().datetime().optional(),
  path: z.string().optional(),
});

export type ErrorResponse = z.infer<typeof ErrorResponseSchema>;

// ============================================================================
// UTILITY TYPES
// ============================================================================

/**
 * ID Response
 * For operations that return just an ID (e.g., after creation)
 */
export const IdResponseSchema = z.object({
  id: z.string().uuid(),
});

export type IdResponse = z.infer<typeof IdResponseSchema>;

/**
 * Bulk Operation Request
 * For batch operations on multiple IDs
 */
export const BulkOperationSchema = z.object({
  ids: z.array(z.string().uuid()).min(1).max(100),
});

export type BulkOperation = z.infer<typeof BulkOperationSchema>;

/**
 * Bulk Operation Response
 * Result of batch operations
 */
export const BulkOperationResponseSchema = z.object({
  success: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  errors: z
    .array(
      z.object({
        id: z.string().uuid(),
        message: z.string(),
      }),
    )
    .optional(),
});

export type BulkOperationResponse = z.infer<typeof BulkOperationResponseSchema>;

/**
 * Exists Check Response
 * For checking if a resource exists
 */
export const ExistsResponseSchema = z.object({
  exists: z.boolean(),
});

export type ExistsResponse = z.infer<typeof ExistsResponseSchema>;

/**
 * Count Response
 * For counting resources
 */
export const CountResponseSchema = z.object({
  count: z.number().int().nonnegative(),
});

export type CountResponse = z.infer<typeof CountResponseSchema>;

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Create pagination metadata from query and total count
 */
export const createPaginationMeta = (page: number, limit: number, total: number): PaginationMeta => {
  const totalPages = Math.ceil(total / limit);

  return {
    page,
    limit,
    total,
    totalPages,
    hasNextPage: page < totalPages,
    hasPreviousPage: page > 1,
  };
};

/**
 * Create paginated response from data and pagination info
 */
export const createPaginatedResponse = <T>(
  data: T[],
  page: number,
  limit: number,
  total: number,
): PaginatedResponse<T> => {
  return {
    data,
    meta: createPaginationMeta(page, limit, total),
  };
};

// ============================================================================
// CURSOR-BASED PAGINATION
// ============================================================================

/**
 * Cursor Paginated Page Schema
 * For React Query infinite scroll patterns
 */
export const createCursorPaginatedPageSchema = <T extends z.ZodTypeAny>(dataSchema: T) => {
  return z.object({
    data: z.array(dataSchema),
    nextCursor: z.string().optional(),
    prevCursor: z.string().optional(),
    hasMore: z.boolean().optional(),
  });
};

/**
 * Cursor Paginated Page Type
 * Generic type for cursor-based pagination in infinite queries
 */
export type CursorPaginatedPage<T> = {
  data: T[];
  nextCursor?: string;
  prevCursor?: string;
  hasMore?: boolean;
};

// ============================================================================
// AUDIT CHANGE TRACKING
// ============================================================================

/**
 * Audit Change Set Schema Factory
 * Creates a schema for tracking before/after changes in audit logs
 */
export const createAuditChangeSetSchema = <T extends z.ZodTypeAny>(valueSchema: T) => {
  return z.object({
    before: z.record(z.string(), valueSchema).optional(),
    after: z.record(z.string(), valueSchema).optional(),
  });
};

/**
 * Default Audit Change Set Schema (with unknown values)
 */
export const AuditChangeSetSchema = z.object({
  before: z.record(z.string(), z.unknown()).optional(),
  after: z.record(z.string(), z.unknown()).optional(),
});

/**
 * Audit Change Set Type
 * Generic type for tracking field-level changes in audit logs
 */
export type AuditChangeSet<T = unknown> = {
  before?: Record<string, T>;
  after?: Record<string, T>;
};

// ============================================================================
// API ERROR HANDLING
// ============================================================================

/**
 * API Error with Response Schema
 * For axios/fetch error handling in React Query hooks
 */
export const ApiErrorWithResponseSchema = z.object({
  message: z.string(),
  response: z
    .object({
      status: z.number(),
      data: z
        .object({
          message: z.string().optional(),
          error: z.string().optional(),
          statusCode: z.number().optional(),
        })
        .optional(),
    })
    .optional(),
  code: z.string().optional(),
  status: z.number().optional(),
});

export type ApiErrorWithResponse = z.infer<typeof ApiErrorWithResponseSchema>;

/**
 * Type guard for API errors with response
 * Use in error handlers to safely access error properties
 */
export function isApiErrorWithResponse(error: unknown): error is ApiErrorWithResponse {
  return (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as Record<string, unknown>).message === 'string'
  );
}

/**
 * Extract error message from API error
 * Safe utility for getting user-friendly error messages
 */
export function getApiErrorMessage(error: unknown, fallback = 'An error occurred'): string {
  if (isApiErrorWithResponse(error)) {
    return error.response?.data?.message || error.message || fallback;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return fallback;
}
